import { useState, type FormEvent } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { caseCategories } from "@/mocks/cases";
import { siteInfo } from "@/mocks/site";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dah6maroh653ivfvnmg0";

type FormStatus = "idle" | "submitting" | "success" | "error";

const submitTips = [
  { id: "tip-01", icon: "ri-user-unfollow-line", text: "头像、昵称、手机号、公司名请提前打码" },
  { id: "tip-02", icon: "ri-file-shield-2-line", text: "先保存原始证据，再考虑公开曝光" },
  { id: "tip-03", icon: "ri-map-pin-user-line", text: "具体地址与可定位信息建议只写城市" },
  { id: "tip-04", icon: "ri-scales-3-line", text: "涉及纠纷时，先咨询专业法律意见" },
];

export default function ContactSection() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = typeof value === "string" ? value.trim() : "";
      if (text) params.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : "提交没有成功，请稍后再试一次，或直接发邮件给我们。",
        );
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络似乎不太稳定，请稍后重试，或直接发邮件联系我们。");
    }
  };

  return (
    <section id="contact" className="w-full border-t border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-[1180px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          index="07"
          title="爆料投稿 · 把你的经历交给我们"
          subtitle="填完下面这张表，编辑部会在 3 到 7 个工作日内整理情节并联系你确认细节。所有可识别信息都会被打码处理。"
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_380px] lg:gap-10">
          <Reveal>
            <form
              id="case-submit-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="nickname" className="mb-2 block text-sm font-medium text-foreground-800">
                    怎么称呼你 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="nickname"
                    name="nickname"
                    type="text"
                    required
                    placeholder="例如：深夜两点半"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground-800">
                    联系邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于确认情节，不会公开"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="category" className="mb-2 block text-sm font-medium text-foreground-800">
                  现场类型
                </label>
                <select
                  id="category"
                  name="category"
                  defaultValue="被抓现行"
                  className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500"
                >
                  {caseCategories
                    .filter((cat) => cat.id !== "all")
                    .map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="mt-5">
                <label htmlFor="summary" className="mb-2 block text-sm font-medium text-foreground-800">
                  情节梗概 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="summary"
                  name="summary"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="用几句话讲清楚发生了什么，比如时间、你们的关系、翻车是怎么被发现的。"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500"
                />
                <p className="mt-1.5 text-[11px] text-foreground-500">最多 500 字，建议控制在 300 字以内</p>
              </div>

              <div className="mt-5">
                <label htmlFor="timeline" className="mb-2 block text-sm font-medium text-foreground-800">
                  关键节点（选填）
                </label>
                <textarea
                  id="timeline"
                  name="timeline"
                  maxLength={500}
                  rows={3}
                  placeholder="例如：他连续三个月说在加班；我发现外卖地址在城东；最后一句谎话是……"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500"
                />
              </div>

              <div className="alt-contact-field" aria-hidden="true">
                <label htmlFor="contact_alt">备用联系电话</label>
                <input
                  id="contact_alt"
                  name="contact_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-7 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <i className={status === "submitting" ? "ri-loader-4-line animate-spin text-base" : "ri-send-plane-fill text-base"} />
                  {status === "submitting" ? "正在投递…" : "提交我的现场"}
                </button>
                <p className="text-[11px] leading-relaxed text-foreground-500">
                  提交即表示你同意我们对情节进行改编与去身份化处理。
                </p>
              </div>

              {status === "success" ? (
                <div className="mt-5 flex items-start gap-3 rounded-md border border-accent-300 bg-accent-50 px-4 py-3">
                  <i className="ri-checkbox-circle-fill text-lg text-accent-700" />
                  <p className="text-sm text-foreground-800">
                    投稿已收到，感谢你把这段经历交给我们。编辑部会尽快整理并联系你确认细节。
                  </p>
                </div>
              ) : null}

              {status === "error" ? (
                <div className="mt-5 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 px-4 py-3">
                  <i className="ri-error-warning-fill text-lg text-primary-600" />
                  <p className="text-sm text-foreground-800">{formError}</p>
                </div>
              ) : null}
            </form>
          </Reveal>

          <Reveal delay={100}>
            <div className="flex h-full flex-col gap-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-shield-check-line text-primary-600" />
                  投稿前请先确认
                </h3>
                <ul className="mt-4 space-y-3">
                  {submitTips.map((tip) => (
                    <li key={tip.id} className="flex items-start gap-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                        <i className={`${tip.icon} text-sm`} />
                      </span>
                      <span className="text-sm leading-relaxed text-foreground-700">{tip.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-customer-service-2-line text-primary-600" />
                  不想填表？直接发邮件
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  也欢迎把整理好的长文发到编辑部邮箱，我们同样会做去身份化处理。
                </p>
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="mt-3 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:border-primary-300"
                >
                  <i className="ri-mail-send-line text-base" />
                  {siteInfo.email}
                </a>
              </div>

              <div className="rounded-lg border border-background-200 bg-foreground-950 p-5">
                <p className="flex items-center gap-2 text-xs font-semibold tracking-wide text-primary-300">
                  <i className="ri-alert-line text-base" />
                  内容说明
                </p>
                <p className="mt-2 text-sm leading-relaxed text-background-200">
                  站内所有案例均为读者投稿改编的虚构情节合集，人物与细节均已模糊处理，不对应任何真实个人。请勿据此对号入座或搜索特定对象。
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}